import React, { useState, useEffect } from 'react';
import { authFetch } from '../../auth';
import './VerificationPage.css';

const COUNTRY_CODES = [
  { code: 'US', name: 'United States' },
  { code: 'CA', name: 'Canada' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'FR', name: 'France' },
  { code: 'DE', name: 'Germany' },
  { code: 'NL', name: 'Netherlands' },
  { code: 'HK', name: 'Hong Kong' },
  { code: 'PR', name: 'Puerto Rico' },
];

const getQueryParam = (name) => {
  var m = window.location.search.match(new RegExp('[?&]' + name + '=([^&]*)'));
  return m ? decodeURIComponent(m[1]) : null;
};

const VerificationPage = ({ user }) => {
  var initialCountry = getQueryParam('country') || 'US';
  var validCodes = COUNTRY_CODES.map(function(c) { return c.code; });
  if (validCodes.indexOf(initialCountry) === -1) initialCountry = 'US';

  const [country, setCountry] = useState(initialCountry);
  const [requirements, setRequirements] = useState(null);
  const [loadingReq, setLoadingReq] = useState(false);
  const [status, setStatus] = useState('idle');
  const [message, setMessage] = useState('');
  const [detail, setDetail] = useState('');
  const [hints, setHints] = useState([]);
  const [verifications, setVerifications] = useState([]);

  const [form, setForm] = useState({
    customerName: '',
    street: '',
    city: '',
    region: '',
    postalCode: '',
    email: user?.email || '',
  });

  useEffect(() => {
    loadVerifications();
    loadRequirements(initialCountry);
  }, []);

  const loadVerifications = async () => {
    try {
      const resp = await authFetch('/api/users/numfree/regulatory');
      if (resp.ok) setVerifications(await resp.json());
    } catch (e) {}
  };

  const loadRequirements = async (countryCode) => {
    setLoadingReq(true);
    setRequirements(null);
    try {
      const resp = await authFetch(`/api/freenumbers/requirements/${countryCode}`);
      if (resp.ok) setRequirements(await resp.json());
    } catch (e) {
      setMessage('Failed to load requirements');
    }
    setLoadingReq(false);
  };

  const handleCountryChange = (code) => {
    setCountry(code);
    loadRequirements(code);
    setStatus('idle');
    setMessage('');
    setDetail('');
    setHints([]);
  };

  const handleSubmit = async () => {
    setStatus('submitting');
    setMessage('');
    setDetail('');
    setHints([]);
    try {
      const resp = await authFetch('/api/users/numfree/regulatory', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          countryCode: country,
          ...form
        })
      });
      const data = await resp.json();
      if (resp.ok) {
        setStatus('success');
        setMessage('Address validated');
        setDetail(data.osmDisplayName ? 'Resolved to: ' + data.osmDisplayName : '');
        loadVerifications();
      } else {
        setStatus('error');
        setMessage(data.error || 'Submission failed');
        setDetail(data.detail || '');

        if (data.error === 'Address not found') {
          setHints([
            { field: 'street', tip: 'Include building number and street name' },
            { field: 'city', tip: 'Use the official city name, not neighborhood' },
            { field: 'postalCode', tip: 'Verify the postal code for this city' },
            { field: 'country', tip: 'Are the city and country matching?' },
          ]);
        } else if (data.error === 'Address validation failed') {
          setHints([
            { field: null, tip: 'Geocoding service is temporarily unavailable. Try again in a moment.' }
          ]);
        }
      }
    } catch (e) {
      setStatus('error');
      setMessage(e.message);
    }
  };

  const handleRetry = () => {
    setStatus('idle');
    setMessage('');
    setDetail('');
    setHints([]);
  };

  const handleDelete = async (countryCode) => {
    if (!window.confirm('Delete verification for ' + countryCode + '?')) return;
    try {
      const resp = await authFetch('/api/users/numfree/regulatory/' + countryCode, { method: 'DELETE' });
      if (resp.ok) loadVerifications();
    } catch (e) {}
  };

  const handleReevaluate = async (bundleSid) => {
    try {
      const resp = await authFetch('/api/users/numfree/regulatory/reevaluate/' + bundleSid, { method: 'POST' });
      if (resp.ok) {
        loadVerifications();
        setMessage('Bundle re-evaluation submitted. You will be notified when approved.');
        setStatus('success');
      } else {
        var err = await resp.json().catch(() => ({}));
        setMessage('Re-evaluation failed');
        setDetail(err.detail || err.error || '');
        setStatus('error');
      }
    } catch (e) {
      setMessage('Re-evaluation failed');
      setDetail(e.message);
      setStatus('error');
    }
  };

  const handleEdit = (v) => {
    setCountry(v.countryCode);
    setForm({
      customerName: v.customerName || '',
      street: v.street || '',
      city: v.city || '',
      region: v.region || '',
      postalCode: v.postalCode || '',
      email: user?.email || '',
    });
    setStatus('idle');
    setMessage('');
    setDetail('');
    setHints([]);
    loadRequirements(v.countryCode);
  };

  const formComplete = form.customerName && form.street && form.city && form.postalCode;

  return (
    <div className="verification-page">
      <h2>Account Verification</h2>
      <p className="verification-desc">Verify your identity to unlock number purchases. Your address is required for regulatory compliance and emergency calling capability.</p>

      <div className="verification-grid">
        <div className="verification-card new-verification">
          <h3>New Verification</h3>

          <div className="form-group">
            <label>Country</label>
            <select value={country} onChange={e => handleCountryChange(e.target.value)}>
              {COUNTRY_CODES.map(c => (
                <option key={c.code} value={c.code}>{c.name} ({c.code})</option>
              ))}
            </select>
          </div>

          {loadingReq && <div className="hint-text">Loading requirements...</div>}
          {requirements && (
            <div className="requirements-info">
              <h4>Requirements for {country}</h4>
              <ul>
                {requirements.addressRequirements && requirements.addressRequirements !== 'none'
                  ? <li>Address required ({requirements.addressRequirements})</li>
                  : <li>No address requirements</li>}
                <li>Type: {requirements.numberType}</li>
                {requirements.beta && <li>Beta program</li>}
              </ul>
            </div>
          )}

          {requirements && (requirements.addressRequirements === 'none' || !requirements.addressRequirements) ? (
            <div className="verification-message success" style={{ marginTop: 12 }}>
              <div className="verification-message-title">No verification required for {country}</div>
              <div className="verification-message-detail">
                Numbers in this country can be purchased without an address bundle.
                Head to the Numbers page to buy one — there's nothing to submit here.
              </div>
            </div>
          ) : (
            <>
              <div className={`form-group ${hints.find(h => h.field === 'customerName') ? 'has-hint' : ''}`}>
                <label>Full Name</label>
                <input type="text" value={form.customerName} onChange={e => setForm({...form, customerName: e.target.value})} placeholder="e.g. John Smith" />
              </div>
              <div className={`form-group ${hints.find(h => h.field === 'street') ? 'has-hint' : ''}`}>
                <label>Street Address</label>
                <input type="text" value={form.street} onChange={e => setForm({...form, street: e.target.value})} placeholder="e.g. 71-75 Shelton Street" />
                {hints.find(h => h.field === 'street') && <span className="field-hint">{hints.find(h => h.field === 'street').tip}</span>}
              </div>
              <div className="form-row">
                <div className={`form-group ${hints.find(h => h.field === 'city') ? 'has-hint' : ''}`}>
                  <label>City</label>
                  <input type="text" value={form.city} onChange={e => setForm({...form, city: e.target.value})} placeholder="City" />
                  {hints.find(h => h.field === 'city') && <span className="field-hint">{hints.find(h => h.field === 'city').tip}</span>}
                </div>
                <div className={`form-group ${hints.find(h => h.field === 'region') ? 'has-hint' : ''}`}>
                  <label>Region / State</label>
                  <input type="text" value={form.region} onChange={e => setForm({...form, region: e.target.value})} placeholder="Region" />
                </div>
              </div>
              <div className="form-row">
                <div className={`form-group ${hints.find(h => h.field === 'postalCode') ? 'has-hint' : ''}`}>
                  <label>Postal Code</label>
                  <input type="text" value={form.postalCode} onChange={e => setForm({...form, postalCode: e.target.value})} placeholder="e.g. WC2H 9JQ" />
                  {hints.find(h => h.field === 'postalCode') && <span className="field-hint">{hints.find(h => h.field === 'postalCode').tip}</span>}
                </div>
                <div className="form-group">
                  <label>Email</label>
                  <input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} disabled />
                </div>
              </div>

              {hints.filter(h => h.field === null).map((h, i) => (
                <div key={i} className="global-hint">{h.tip}</div>
              ))}

              {message && (
                <div className={`verification-message ${status}`}>
                  <div className="verification-message-title">{message}</div>
                  {detail && <div className="verification-message-detail">{detail}</div>}
                </div>
              )}

              <button className="btn btn-primary"
                onClick={status === 'error' ? handleRetry : handleSubmit}
                disabled={status === 'submitting' || !formComplete}>
                {status === 'submitting' ? 'Validating...' : status === 'error' ? 'Try Again' : 'Submit Verification'}
              </button>
            </>
          )}
        </div>

        <div className="verification-card existing-verifications">
          <h3>Existing Verifications</h3>
          {verifications.length === 0 ? (
            <p className="empty-state">No verifications yet.</p>
          ) : (
            <div className="verification-list">
              {verifications.map((v, i) => (
                <div key={i} className={`verification-item status-${v.status}`}>
                  <div className="verification-item-header">
                    <strong>{v.countryCode}</strong>
                    <span className="verification-status">{v.bundleStatus || v.status}</span>
                  </div>
                  <div className="verification-item-details">
                    {v.customerName} · {v.city}, {v.postalCode}
                  </div>
                  {v.osmValidated && (
                    <div className="verification-item-validation">
                      Geocoding verified
                    </div>
                  )}
                  {v.bundleSid && (
                    <div className="verification-item-bundle">
                      <span className="bundle-label">Bundle</span>
                      <span className={`bundle-status bundle-${v.bundleStatus || 'unknown'}`}>
                        {v.bundleStatus === 'approved' ? 'Approved' : v.bundleStatus === 'rejected' ? 'Rejected' : v.bundleStatus === 'failed' ? 'Failed' : 'In Review'}
                      </span>
                      {v.supportingDocs && v.supportingDocs.length > 0 && (
                        <span className="bundle-docs"> · {v.supportingDocs.length} doc{v.supportingDocs.length !== 1 ? 's' : ''}</span>
                      )}
                      {v.bundleStatus === 'rejected' && v.rejectionReason && (
                        <div className="bundle-rejection-reason">Reason: {v.rejectionReason}</div>
                      )}
                      {v.bundleStatus === 'rejected' && (
                        <button className="btn-retry" onClick={() => handleReevaluate(v.bundleSid)}>Retry</button>
                      )}
                    </div>
                  )}
                  <div className="verification-item-actions">
                    <button className="btn-edit" onClick={() => handleEdit(v)}>Edit</button>
                    <button className="btn-delete" onClick={() => handleDelete(v.countryCode)}>Delete</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VerificationPage;
